import {Formik} from 'formik';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import CustomHeader from '../../../../../components/mazraeKit/headers/homeHeader';
import SpinnerLoading from '../../../../../components/SpinnerLoading';
import useGetListLoanRequest from '../../../../../services/hookService/getListLoanRequest/useGetListLoanRequest';
import useGetListLoanRule from '../../../../../services/hookService/getListLoanRule/useGetListLoanRule';
import {WarrantyDocumentType} from '../../../../../services/hookService/getLoanRequestRule/types';
import useGetLoanRequestRule from '../../../../../services/hookService/getLoanRequestRule/useGetLoanRequestRule';
import usePatchGuarantor from '../../../../../services/hookService/patchGuarantor/usePatchGuarantor';
import CancelLoanBottomSheet from '../../cancelLoanRequest/CancelLoanBottomSheet';
import CancelLoanConfirmModal from '../../cancelLoanRequest/CancelLoanConfirmModal';

import MultiStepComponent from '../../MultiStepComponent';
import {receiveCreditSteps} from '../../receiveCreditSteps';
import {useMultiStepForm} from '../../useMultiStepForm';
import {removeFalsyFields} from '../utils/removeFalsyFields';
import LoanOwnerForm from './Forms/LoanOwnerForm';
import {createBankDocsValidationSchema} from './Forms/MultiPersonForm/DocFormValidation';
import MultiPersonPreview from './Forms/MultiPersonForm/MultiPersonform';
import {PersonPrefix} from './Forms/MultiPersonForm/MultiPersonValidation';

export type JobTypes = 1 | 2 | 3; // 1: Employee, 2: Self Employment, 3: Special Job
export type GuarantorRoleTypes = 0 | 1 | 2 | 9; // 1 : FIRST_GUARANTOR , 2: SECOND_GUARANTOR , 3: SELF_GUARANTOR

export type CheckTypes = 'paper' | 'digital';

export interface IPersonFormData {
  name?: string;
  cellphone_number?: string;
  job_type: JobTypes | 0;
  checkType: CheckTypes | '';
  loan_request: string;
  loan_rule: string;
  is_check_registered: boolean;
  first_digital_cheque_id: string;
  second_digital_cheque_id: string;
  third_digital_cheque_id: string;
  first_check_image: File | null;
  second_check_image: File | null;
  third_check_image: File | null;
  first_sayadi_cheque_id: string;
  second_sayadi_cheque_id: string;
  third_sayadi_cheque_id: string;
  _allJobImagesUploaded: boolean;
  national_code: string;
  role: GuarantorRoleTypes;
}

export type checkDeliveryMethodTypes = 'sent' | 'delivered' | '';

export interface IGlobalDocsFormData {
  parcel_tracking_code: string;
  check_delivery_method: checkDeliveryMethodTypes;
  address: string;
  zip_code: string;
}
export interface IBankDocsGeneralForm {
  global: IGlobalDocsFormData;
  self: IPersonFormData;
  'Guarantor-0': IPersonFormData;
  'Guarantor-1': IPersonFormData;
}

const LoanDocsPage = () => {
  const navigate = useNavigate();
  const [editingPerson, setEditingPerson] = useState<PersonPrefix | null>(null);
  // ---------------- Cancel Loan States ----------------
  const [isOpenCancelLoanBtm, setIsOpenCancelLoanBtm] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const {stepNumber, goNext} = useMultiStepForm();

  const {data: listLoanRequest, isLoading: isLoadingListLoanRequest} =
    useGetListLoanRequest({is_finished: false});

  const {data: loanRequestRules, isLoading: isLoadingLoanRequestRules} =
    useGetLoanRequestRule(listLoanRequest?.guid, 'cache');

  const {patchGuarantor, isPatchGuarantorLoading} = usePatchGuarantor();

  const {loanRule, isLoadingLoanRule} = useGetListLoanRule(
    listLoanRequest?.guid,
    true,
  );

  const loanItem = listLoanRequest as ILoanRequestItem | undefined;
  const isLoading =
    isLoadingListLoanRequest || isLoadingLoanRequestRules || isLoadingLoanRule;
  const warrantyType = loanRequestRules?.warranty_document_type;

  const createEmptyPersonData = (
    guarantorRole: GuarantorRoleTypes,
  ): IPersonFormData => ({
    name: '',
    cellphone_number: '',
    job_type: 0,
    checkType: '',
    loan_request: listLoanRequest?.guid ?? '',
    loan_rule: loanRule?.loan_type_rules?.[0]?.guid ?? '',
    is_check_registered: false,
    first_digital_cheque_id: '',
    second_digital_cheque_id: '',
    third_digital_cheque_id: '',
    _allJobImagesUploaded: false,
    first_check_image: null,
    second_check_image: null,
    third_check_image: null,
    first_sayadi_cheque_id: '',
    second_sayadi_cheque_id: '',
    third_sayadi_cheque_id: '',
    national_code: '',
    role: guarantorRole,
  });

  // Nested initial values with hyphenated keys
  const initialValues: IBankDocsGeneralForm = {
    global: {
      parcel_tracking_code: '',
      check_delivery_method: '',
      zip_code: '',
      address: '',
    },
    self: createEmptyPersonData(9), // self always check owner
    'Guarantor-0': createEmptyPersonData(1),
    'Guarantor-1': createEmptyPersonData(2),
  };

  const handleSubmit = async (values: IBankDocsGeneralForm) => {
    const global = values.global;

    const buildGuarantors = (): IPersonFormData[] => {
      switch (warrantyType) {
        case WarrantyDocumentType.OWN_CHECK: // Self only
          return [values.self];

        case WarrantyDocumentType.ONE_GUARANTOR: // One guarantor
          return [values['Guarantor-0']];

        case WarrantyDocumentType.TWO_GUARANTORS: // Two guarantors
          return [values['Guarantor-0'], values['Guarantor-1']];

        case WarrantyDocumentType.OWN_CHECK_SINGLE_GUARANTOR: // Self + one guarantor
          return [values.self, values['Guarantor-0']];

        case WarrantyDocumentType.OWN_CHECK_TWO_GUARANTORS: // Self + two guarantors
          return [values.self, values['Guarantor-0'], values['Guarantor-1']];

        default:
          return [];
      }
    };

    const guarantors = buildGuarantors();

    // Build the complete payload with guarantors array
    const buildPayload = () => {
      const guarantorsArray = guarantors.map(g => ({
        loan_rule: g.loan_rule,
        role: g.role,
        ...(g.first_digital_cheque_id && {
          first_digital_cheque_id: g.first_digital_cheque_id,
        }),
        ...(g.second_digital_cheque_id && {
          second_digital_cheque_id: g.second_digital_cheque_id,
        }),
        ...(g.third_digital_cheque_id && {
          third_digital_cheque_id: g.third_digital_cheque_id,
        }),
        ...(g.name && {name: g.name}),
        ...(g.cellphone_number && {cellphone_number: g.cellphone_number}),
        ...(g.national_code && {national_code: g.national_code}),
        ...(g.first_sayadi_cheque_id && {
          first_sayadi_cheque_id: g.first_sayadi_cheque_id,
        }),
        ...(g.second_sayadi_cheque_id && {
          second_sayadi_cheque_id: g.second_sayadi_cheque_id,
        }),
        ...(g.third_sayadi_cheque_id && {
          third_sayadi_cheque_id: g.third_sayadi_cheque_id,
        }),
        ...(g.job_type && {job_type: g.job_type}),
      }));

      return {
        ...(global?.zip_code && {zip_code: global?.zip_code}),
        ...(global?.address && {address: global?.address}),
        ...(global?.parcel_tracking_code && {
          parcel_tracking_code: global?.parcel_tracking_code,
        }),
        guarantors: guarantorsArray,
      };
    };

    const payload = buildPayload();

    patchGuarantor(removeFalsyFields(payload) as any, {
      onSuccess: () => {
        goNext(false);
      },
      onError: (err: any) => {
        toast.error(err);
      },
    });
  };

  return (
    <div className="flex flex-col w-full bg-white h-dvh">
      <CustomHeader
        preComponent={() => (
          <div
            className="relative flex items-center gap-2 cursor-pointer"
            onClick={() =>
              editingPerson
                ? setEditingPerson(null)
                : navigate('/my-credits?tab=current', {replace: true})
            }
          >
            <i className="icon-nameArrow---Right text-[25px] text-gray-700" />
            <span className="font-medium">
              {editingPerson ? getHeaderTitle(editingPerson) : 'تکمیل مدارک'}
            </span>
          </div>
        )}
        postComponent={() => (
          <>
            {!editingPerson && (
              <i
                className="icon-nameInfo-menu-1 text-[25px] text-gray-700"
                onClick={() => setIsOpenCancelLoanBtm(true)}
              />
            )}
          </>
        )}
      />

      <div className="flex flex-col w-full h-full gap-3 px-6 py-3 overflow-y-auto">
        {isLoading && (
          <div className="grid h-full place-content-center">
            <SpinnerLoading />
          </div>
        )}

        {!isLoading && loanItem && (
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={createBankDocsValidationSchema(
              warrantyType,
              loanRequestRules?.need_extra_document ?? false,
              loanRequestRules,
            )}
            validateOnChange
            validateOnBlur
            validateOnMount={false}
            enableReinitialize
            context={{
              skipConditionalFields: false,
            }}
          >
            {({handleSubmit}) => (
              <form
                id="loan-docs-form"
                onSubmit={handleSubmit}
                className="flex flex-col h-full"
              >
                {!editingPerson && (
                  <MultiStepComponent
                    steps={receiveCreditSteps}
                    currentStep={stepNumber}
                  />
                )}

                <section className="flex flex-col gap-[1rem] mt-3 pb-20">
                  {warrantyType ? (
                    <>
                      {!editingPerson && <LoanOwnerForm />}
                      <MultiPersonPreview
                        loanRule={loanRule}
                        loanRequestGuid={listLoanRequest?.guid!}
                        loanRequestRules={loanRequestRules!}
                        warrantyType={warrantyType}
                        isSubmitting={isPatchGuarantorLoading}
                        setEditingPerson={setEditingPerson}
                        editingPerson={editingPerson}
                      />
                    </>
                  ) : (
                    <div className="flex items-center justify-center p-4 text-red-500 rounded-lg bg-red-50 text-md">
                      نوع فرم نامعتبر است. لطفا با پشتیبانی تماس بگیرید
                    </div>
                  )}
                </section>
              </form>
            )}
          </Formik>
        )}
      </div>

      <CancelLoanBottomSheet
        isOpen={isOpenCancelLoanBtm}
        setIsOpen={setIsOpenCancelLoanBtm}
        onConfirm={() => setShowConfirmModal(true)}
      />
      <CancelLoanConfirmModal
        isOpen={showConfirmModal}
        setIsOpen={setShowConfirmModal}
        loanGuid={listLoanRequest?.guid!}
      />
    </div>
  );
};

export default LoanDocsPage;

const getHeaderTitle = (personPrefix: PersonPrefix): string => {
  switch (personPrefix) {
    case 'self':
      return 'ثبت چک به نام خود فرد';
    case 'Guarantor-0':
      return 'افزودن ضامن اول';
    case 'Guarantor-1':
      return 'افزودن ضامن دوم';
    default:
      return 'خطا';
  }
};
